import { api } from "api/client";
import { API_ENDPOINTS } from "config/api.config";
import {
  PushMessage,
  SendPushRequest,
  Campaign,
  CampaignCreateRequest,
  Segment,
  SegmentCreateRequest,
  PushHistoryFilter,
  PushStatistics,
  PushReport,
  PushHistoryGroupedResponse,
  SendTestPushRequest
} from "types/push.types";
import { ListResponse, SuccessResponse } from "types/api.types";
import { url } from "utils/helpers";

export const pushApi = {
  send: async (
    data: SendPushRequest
  ): Promise<SuccessResponse & { message_id: string }> => {
    const response = await api.post<SuccessResponse & { message_id: string }>(
      API_ENDPOINTS.push.send,
      data
    );
    return response.data;
  },

  sendTest: async (
    data: SendTestPushRequest
  ): Promise<SuccessResponse & { message_id?: string }> => {
    const response = await api.post<SuccessResponse & { message_id?: string }>(
      API_ENDPOINTS.push.sendTest,
      data
    );
    return response.data;
  },

  getStatus: async (messageId: string): Promise<PushStatistics> => {
    const response = await api.get<PushStatistics>(
      API_ENDPOINTS.push.getStatus(messageId)
    );
    return response.data;
  },

  getHistory: async (
    filter?: PushHistoryFilter
  ): Promise<ListResponse<PushMessage>> => {
    const params = filter ? url.buildQueryString(filter) : "";
    const response = await api.get<ListResponse<PushMessage>>(
      `${API_ENDPOINTS.push.history}${params}`
    );
    return response.data;
  },

  listCampaigns: async (
    appId?: number,
    limit: number = 20,
    offset: number = 0
  ): Promise<ListResponse<Campaign>> => {
    const response = await api.get<ListResponse<Campaign>>(
      API_ENDPOINTS.campaigns.list,
      { app_id: appId, limit, offset }
    );
    return response.data;
  },

  getCampaign: async (id: number): Promise<Campaign> => {
    const response = await api.get<Campaign>(API_ENDPOINTS.campaigns.get(id));
    return response.data;
  },

  createCampaign: async (
    data: CampaignCreateRequest
  ): Promise<SuccessResponse & { campaign: Campaign }> => {
    const response = await api.post<SuccessResponse & { campaign: Campaign }>(
      API_ENDPOINTS.campaigns.create,
      data
    );
    return response.data;
  },

  updateCampaign: async (
    id: number,
    data: Partial<CampaignCreateRequest>
  ): Promise<SuccessResponse & { campaign: Campaign }> => {
    const response = await api.put<SuccessResponse & { campaign: Campaign }>(
      API_ENDPOINTS.campaigns.update(id),
      data
    );
    return response.data;
  },

  listSegments: async (
    appId?: number,
    limit: number = 20,
    offset: number = 0
  ): Promise<ListResponse<Segment>> => {
    const response = await api.get<ListResponse<Segment>>(
      API_ENDPOINTS.segments.list,
      { app_id: appId, limit, offset }
    );
    return response.data;
  },

  getSegment: async (id: number): Promise<Segment> => {
    const response = await api.get<Segment>(API_ENDPOINTS.segments.get(id));
    return response.data;
  },

  createSegment: async (
    data: SegmentCreateRequest
  ): Promise<SuccessResponse & { segment: Segment }> => {
    const response = await api.post<SuccessResponse & { segment: Segment }>(
      API_ENDPOINTS.segments.create,
      data
    );
    return response.data;
  },

  testSegment: async (
    data: SegmentCreateRequest
  ): Promise<{ user_count: number; users: any[] }> => {
    const response = await api.post<{ user_count: number; users: any[] }>(
      API_ENDPOINTS.segments.test,
      data
    );
    return response.data;
  },

  updateSegment: async (
    id: number,
    data: Partial<SegmentCreateRequest>
  ): Promise<SuccessResponse & { segment: Segment }> => {
    const response = await api.put<SuccessResponse & { segment: Segment }>(
      API_ENDPOINTS.segments.update(id),
      data
    );
    return response.data;
  },

  deleteSegment: async (id: number): Promise<SuccessResponse> => {
    const response = await api.delete<SuccessResponse>(
      API_ENDPOINTS.segments.delete(id)
    );
    return response.data;
  },

  deleteCampaign: async (id: number): Promise<SuccessResponse> => {
    const response = await api.delete<SuccessResponse>(
      API_ENDPOINTS.campaigns.delete(id)
    );
    return response.data;
  },

  getHistoryGrouped: async (
    filter?: PushHistoryFilter,
    signal?: AbortSignal
  ): Promise<PushHistoryGroupedResponse> => {
    const params = filter
      ? url.buildQueryString({ ...filter, grouped: 'true' })
      : '?grouped=true';
    const response = await api.get<PushHistoryGroupedResponse>(
      `${API_ENDPOINTS.push.history}${params}`,
      undefined,
      { signal }
    );
    return response.data;
  },

  getPushReport: async (messageId: string): Promise<PushReport> => {
    const response = await api.get<PushReport>(
      API_ENDPOINTS.stats.pushReport(messageId)
    );
    return response.data;
  },
};
